(() => {
  const CUSTOM_ELEMENT_NAME = 'ag-blog-preview';
  const MOBILE_MEDIA_QUERY = '(max-width: 989px)';
  const SLIDER_SELECTOR = '[data-blog-preview-slider]';
  const PAGINATION_SELECTOR = '[data-blog-preview-pagination]';
  const LOAD_MORE_SELECTOR = '[data-blog-preview-load-more]';
  const TEMPLATE_SELECTOR = '[data-blog-preview-template]';
  const TRACK_SELECTOR = '.swiper-wrapper';
  const COLUMNS_PROPERTY = '--ag-blog-preview-columns';
  const MAX_DESKTOP_COLUMNS = 3;
  const DEFAULT_BATCH_SIZE = 1;
  const INTEGER_RADIX = 10;
  const MOBILE_SPACE_BETWEEN = 24;

  class AgBlogPreview extends HTMLElement {
    constructor() {
      super();
      this.breakpoint = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.swiper = null;
      this.slider = null;
      this.pagination = null;
      this.loadMoreButton = null;
      this.template = null;
      this.track = null;
      this.isInitialized = false;
      this.visibility = null;

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleLoadMore = this.handleLoadMore.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.isInitialized = true;
      this.slider = this.querySelector(SLIDER_SELECTOR);
      this.pagination = this.querySelector(PAGINATION_SELECTOR);
      this.loadMoreButton = this.querySelector(LOAD_MORE_SELECTOR);
      this.template = this.querySelector(TEMPLATE_SELECTOR);
      this.track = this.slider?.querySelector(TRACK_SELECTOR) || null;

      this.breakpoint.addEventListener('change', this.handleBreakpointChange);
      window.addEventListener('load', this.handleWindowLoad);
      this.loadMoreButton?.addEventListener('click', this.handleLoadMore);
      this.visibility = window.AGSliderVisibility ? new window.AGSliderVisibility(this, () => this.syncSwiper()) : null;
      this.syncSwiper();
    }

    disconnectedCallback() {
      if (!this.isInitialized) {
        return;
      }

      this.breakpoint.removeEventListener('change', this.handleBreakpointChange);
      window.removeEventListener('load', this.handleWindowLoad);
      this.loadMoreButton?.removeEventListener('click', this.handleLoadMore);
      this.visibility?.disconnect();
      this.visibility = null;
      this.destroySwiper();

      this.slider = null;
      this.pagination = null;
      this.loadMoreButton = null;
      this.template = null;
      this.track = null;
      this.isInitialized = false;
    }

    handleBreakpointChange() {
      this.syncSwiper();
    }

    handleWindowLoad() {
      this.syncSwiper();
    }

    handleLoadMore() {
      if (!this.loadMoreButton || !this.template || !this.track || !this.slider) {
        return;
      }

      const batchSize = Math.max(
        Number.parseInt(this.loadMoreButton.dataset.blogPreviewBatchSize, INTEGER_RADIX) || DEFAULT_BATCH_SIZE,
        DEFAULT_BATCH_SIZE
      );
      const previousSlideCount = this.track.children.length;
      const hadFocus = document.activeElement === this.loadMoreButton;
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < batchSize; index += 1) {
        const nextSlide = this.template.content.firstElementChild;
        if (!nextSlide) break;
        fragment.appendChild(nextSlide);
      }

      if (!fragment.childElementCount) {
        this.removeLoadMoreControls();
        return;
      }

      const firstAddedSlide = fragment.firstElementChild;
      this.track.appendChild(fragment);
      this.updateDesktopColumns();
      this.syncSwiper();

      if (this.swiper) {
        this.swiper.update();
        this.swiper.pagination?.render();
        this.swiper.pagination?.update();
        this.swiper.slideTo(previousSlideCount);
      }

      if (!this.template.content.firstElementChild) {
        this.removeLoadMoreControls();
      }
      if (hadFocus) firstAddedSlide.querySelector('a[href], button:not([disabled])')?.focus();
    }

    updateDesktopColumns() {
      this.slider.style.setProperty(
        COLUMNS_PROPERTY,
        Math.min(this.track.children.length, MAX_DESKTOP_COLUMNS)
      );
    }

    removeLoadMoreControls() {
      this.loadMoreButton?.removeEventListener('click', this.handleLoadMore);
      this.loadMoreButton?.remove();
      this.template?.remove();
      this.loadMoreButton = null;
      this.template = null;
    }

    syncSwiper() {
      if (!this.slider || !this.pagination) {
        return;
      }

      const canSlide = this.breakpoint.matches && this.track?.children.length > 1;
      this.pagination.hidden = !canSlide;
      if (!canSlide) {
        this.destroySwiper();
        return;
      }

      if (this.visibility && !this.visibility.ready) return;
      if (!this.swiper && typeof window.Swiper !== 'undefined') {
        this.swiper = new window.Swiper(this.slider, {
          slidesPerView: 'auto',
          spaceBetween: MOBILE_SPACE_BETWEEN,
          watchOverflow: true,
          pagination: {
            el: this.pagination,
            clickable: true,
          },
        });
      }
    }

    destroySwiper() {
      if (!this.swiper) {
        return;
      }

      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  if (!customElements.get(CUSTOM_ELEMENT_NAME)) {
    customElements.define(CUSTOM_ELEMENT_NAME, AgBlogPreview);
  }
})();
